import { useState, useEffect } from "react";
import {useStyles} from "../styles/styles";
import { useRequestUrl } from "../context/contextHooks";
import API from "../http/API";
import { validateVerify } from "./paymentFunctions";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import TextField from "@mui/material/TextField";
import { PatternFormat } from "react-number-format";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import React from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

const Verify = (props) => {
    const open = props.open;
    const setOpen = props.setOpen;
    const paymentRequest = props.paymentRequest;
    const paymentFields = props.paymentFields;
    const court = props.court
    const handleVerificationReturned = props.handleVerificationReturned;
    const [requestUrl,] = useRequestUrl();
    const classes = useStyles;
    const [loading, setLoading] = useState(false);
    const [actionResponse, setActionResponse] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [channel, setChannel] = useState('sms');
    const [phone, setPhone] = useState(
        paymentFields?.phone ? paymentFields.phone : paymentRequest?.phone ? paymentRequest.phone : ''
    );
    const [code, setCode] = useState(null);
    const [verifyFields, setVerifyFields] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [verifySent, setVerifySent] = useState(false);

    const clearMessages = () => {
        setActionResponse(null);
        setErrorMsg(null);
    }

    const handleVerify = () => {
        const fields = {channel: channel, phone: phone};
        const errors = validateVerify(fields);
        if (errors) {
            setErrorMsg(errors);
        } else {
            setVerifyFields(fields);
            setVerifying(true);
            clearMessages();
            setLoading(true);
            const url = `${requestUrl}/sendVerifyRequest`;
            const data = JSON.stringify(fields);
            API.postAction(url,data,"sendVerifyRequest",null,setActionResponse)
        }
    }

    const handleSubmit = () => {
        if (!code) {
            setErrorMsg('You must enter a verification code');
        } else {
            const toVal = verifyFields.phone;
            handleVerificationReturned({verificationTo: toVal, verificationCode: code.trim()});
        }
    }

    const handleCloseDialog = (event, reason) => {
        if (reason && reason === 'backdropClick') return;
        setOpen(false);
    }

    const handleChannelChange = (event) => {
        setChannel(event.target.value);
    }

    const handlePhoneChange = (values) => {
        const val = values?.value;
        if (val && val.length === 10) setPhone(values.value);
    }

    const handleCodeChange = (event) => {
        setCode(event.target.value);
    }

    useEffect(() => {
        if (loading && actionResponse) {
            if (actionResponse.error) {
                setErrorMsg(actionResponse.errorMessage);
            } else if (actionResponse.requestType === 'sendVerifyRequest') {
                setVerifySent(true);
            }
            setLoading(false);
            setVerifying(false);
        }
    }, [actionResponse, loading])

    return (
        <Dialog
            open={open}
            onClose={handleCloseDialog}
            maxWidth='sm'
            fullWidth
            sx={classes.dialog}
            PaperProps={{sx: classes.listContainer}}
        >
            <DialogTitle  sx={classes.title}>Verify Your Identity</DialogTitle>
            { verifying &&
                <Backdrop open={verifying} sx={classes.backdrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            }
            { errorMsg &&
                <Alert severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                    <AlertTitle>Error!</AlertTitle>
                    {errorMsg}
                </Alert>
            }
            <DialogContent dividers sx={classes.listContainer}>
                <Grid container spacing={2} sx={{...classes.text, padding: '.5rem 1.5rem'}}>
                    <Grid item xs={12} sx={{marginBottom: '.5rem'}}>
                        { !verifySent ?
                            <>
                                <Typography variant='subtitle1' sx={{fontSize: '1.2rpm', lineHeight: '1.5rem'}}>
                                    Before we can securely process your payment to {court.courtName}, we first need to verify
                                    that you are a person making a payment. Please enter the phone number for a phone in your possession that
                                    we can send a verification code to, and choose whether to receive the code in a text message or an
                                    automated voice call.
                                </Typography>
                                <Typography variant='subtitle1' sx={{fontSize: '1.2rpm', lineHeight: '1.5rem', marginTop: '.5rem'}}>
                                    Note: Only phone numbers in the U.S., Canada, and Puerto Rico are supported.
                                </Typography>
                                <Typography variant='subtitle1' sx={{fontSize: '1.2rpm', lineHeight: '1.5rem', marginTop: '.5rem', fontStyle: 'italic'}}>
                                    By clicking "Verify" I consent to receiving text messages/emails from Avenu Insights & Analytics.
                                </Typography>
                            </> :
                            <Typography variant='h6' >
                                After you receive the code, enter it in the Verification Code field.
                                If the code you enter does not match the one sent, you will be asked to submit your
                                payment again.
                            </Typography>
                        }
                    </Grid>
                    { !verifySent ?
                        <>
                            <Grid item xs={6} >
                                <FormControl fullWidth sx={classes.formControl}>
                                    <FormLabel shrink required sx={classes.inputLabel} htmlFor='phone'>
                                        Phone Number
                                    </FormLabel>
                                    <PatternFormat
                                        id='phone'
                                        name='phone'
                                        customInput={TextField}
                                        variant='outlined'
                                        format='(###) ###-####'
                                        inputProps={{ sx: {...classes.textbox} }}
                                        value={phone}
                                        onValueChange={(values) => handlePhoneChange(values)}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} >
                                <FormControl fullWidth sx={classes.formControl}>
                                    <FormLabel shrink required sx={classes.inputLabel} htmlFor='channel'>
                                        Send the Verification Code by:
                                    </FormLabel>
                                    <RadioGroup
                                        defaultValue='sms'
                                        value={channel}
                                        name='channel'
                                        id='channel'
                                        onChange={handleChannelChange}
                                    >
                                        <FormControlLabel control={<Radio />} label="A Text to My Phone" value="sms" />
                                        <FormControlLabel control={<Radio />} label='A Voice Call to My Phone' value="call" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </> :
                        <Grid item xs={6} >
                            <FormControl fullWidth sx={classes.formControl}>
                                <FormLabel shrink required sx={classes.inputLabel} htmlFor='code'>
                                    Verification Code
                                </FormLabel>
                                <TextField
                                    id='code'
                                    name='code'
                                    variant='outlined'
                                    fullWidth
                                    inputProps={{sx: classes.textbox}}
                                    value={code}
                                    onChange={handleCodeChange}
                                />
                            </FormControl>
                        </Grid>
                    }
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: '1.5rem'}}>
                <Button
                    onClick={handleCloseDialog}
                    variant='outlined'
                    color="primary"
                    autoFocus
                    sx={classes.tinyButton}
                >
                    Cancel
                </Button>
                { verifySent ?
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        autoFocus
                        variant='contained'
                        sx={classes.tinyButton}
                    >
                        Submit Payment
                    </Button> :
                    <>
                        { !verifying &&
                            <Button
                                onClick={handleVerify}
                                color="primary"
                                autoFocus
                                variant='contained'
                                sx={classes.tinyButton}
                            >
                                Verify
                            </Button>
                        }
                    </>
                }
            </DialogActions>
        </Dialog>
    );

}

export default Verify;
